import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import {
  useChartingBeforeUnload,
  useChartingLeaveClickGuard,
} from './charting-leave-blocker.hook'

type PendingChartingLeave =
  | { kind: 'charting-tab'; tab: string }
  | { kind: 'route'; path: string; state: Record<string, unknown> | null }
import { Container } from 'react-bootstrap'
import type { TreatmentPlanNavigationHandle } from '@components/pages/Charting/TreatmentPlan/treatment-plan-navigation.types'

import ChartingMain from '@components/pages/Charting/Dental/ChartingMain.component'
import { type EmbeddedXraySelectedFile } from '@components/common/EmbeddedXrayViewer'
import { preloadChartingTabAssets } from './charting-tab-preload.utils'
import PerioPage from './Perio.page'
import PerioVoiceStatus from '@components/pages/Charting/Perio/PerioVoiceStatus.component'
import DentalVoiceStatus from '@components/pages/Charting/Dental/DentalVoiceStatus.component'
import TreatmentPlanPage from './TreatmentPlan.page'
import HistoryPage from './History.page'
import {
  PerioChartSectionData,
  PerioSection,
} from '@dataTypes/chartingFeature.types'
import './Charting.css'
import {
  getChartingRouteTabFromPathname,
  resolveChartingNoteOverlayPayload,
  type ChartingRouteTab,
} from './chartingNoteOverlay.util'
import ChartingTopBar from './ChartingTopBar'
import ChartingSkeleton from './ChartingSkeleton.page'
import ConfirmationModal from '@components/common/ConfirmationModal/ConfirmationModal.component'
import ChartingUnsavedChangesModal from '@components/pages/Charting/Modals/ChartingUnsavedChangesModal.component'
import Button from '@components/common/Button/Button.component'
import {
  formatDentalDataSummary,
  type DentalDataSummary,
} from '@utils/dental-data-summary.utils'
import DentalChartSummaryContent from '@components/pages/Charting/Dental/DentalChartSummaryContent.component'
import { useActiveDentalChart } from '@hooks/useActiveDentalChart'
import { useActivePerioChart } from '@hooks/useActivePerioChart'
import { usePerioStreamingVoice } from '@hooks/usePerioStreamingVoice'
import { useDentalStreamingVoice } from '@hooks/useDentalStreamingVoice'
import { createEmptyPerioChartData } from '@utils/perio.utils'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  fetchAllPatients,
  fetchChartingbyPatientId,
  fetchInsurancePlansByPatientId,
} from '@store/actions'
import {
  setCompareCharts,
  setCreateDentalChart,
  setCreatePerioChart,
  setDentalUpdated,
  setPerioUpdated,
  setPreferCreateDentalChart,
  setPreferCreatePerioChart,
  setSelectedDentalChart,
  setSelectedPerioChart,
  clearAllChartingData,
  resetPerioChart,
  resetDentalChart,
} from '@store/reducers/charting.reducer'
import { GridComponent } from '@syncfusion/ej2-react-grids'
import { EmptyDentalChart, EmptyPerioChart } from '@constants/Charting'
import GoBackButton from '@components/common/GoBackButton/GoBackButton.component'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import {
  toggleXrayPanel,
  setXrayPanelOpen,
  requestPatientSelection,
} from '@store/reducers/uiSlice.reducer'
import { useClinicProviders } from '@hooks'
import {
  prefetchClinicChartingPreferences,
  useClinicChartingPreferences,
} from '@hooks/useClinicChartingPreferences'
import {
  defaultChartingViewToLayers,
  type ChartingLayersState,
} from '@utils/default-charting-view.utils'
import { ChartingLayerKey } from '@dataTypes/chartingPreferences.type'
import AlertMessage from '@components/layout/Modals/AlertMessage/AlertMessage.component'
import { EmptyState } from '@components/ui'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createFailedToast } from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  useNoteEditorOverlay,
  getBackButtonLabelForOrigin,
} from '@components/common/NoteEditorOverlay'
import { useChartAutoSave } from '@hooks/useChartAutoSave'
import { useTxPlanAutoCompleteOnChartSave } from '@hooks/useTxPlanAutoCompleteOnChartSave'
import {
  CHART_DENTAL_CONTENT_REQUIRED_ERROR,
  CHART_PATIENT_REQUIRED_ERROR,
  CHART_PERIO_CONTENT_REQUIRED_ERROR,
  CHART_PROVIDER_INVALID_ERROR,
  CHART_PROVIDER_REQUIRED_ERROR,
  CHART_TITLE_REQUIRED_ERROR,
  buildManualSaveValidationToastMessage,
  hasChartingInformationFieldErrors,
} from '@utils/chart-validation.utils'
import { useChartPersist } from './useChartPersist'
import { isAlignedPersistedDentalEditSession } from '@utils/active-dental-chart.utils'
import { isAlignedPersistedPerioEditSession } from '@utils/active-perio-chart.utils'
import {
  getUniqueChartTitleResolution,
  hasDentalChartableContent,
  hasPerioChartableContent,
  type UniqueChartTitleResolution,
} from '@utils/chart-save.utils'
import { clearChartHistorySelectionClasses } from '@utils/chart-history-selection.utils'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'

interface PendingSaveConfirmation {
  type: 'dental' | 'perio'
  chartId: string
  titleResolution: UniqueChartTitleResolution
  dentalSummary?: DentalDataSummary
}

const GLOBAL_LOADING_CONTAINER = 'body'
const CHARTING_LOADING_CONTAINER = '.charting-loading-container'
const TAB_SKELETON_DURATION_MS = 500

function waitForChartingTabPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve()
      })
    })
  })
}

function waitMs(durationMs: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, durationMs)
  })
}

const ChartingPage: React.FC = () => {
  useClinicProviders()
  const location = useLocation()
  const chartingNotesBackLabel = useMemo(
    (): string =>
      getBackButtonLabelForOrigin({
        path: location.pathname || '/charting',
      }),
    [location.pathname],
  )
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const gridRef = useRef<GridComponent>(null)
  const isMountedRef = useRef<boolean>(true)
  const tryAutoSaveBeforePromptRef = useRef<() => Promise<boolean>>(
    async () => false,
  )
  const autoSaveEnabledRef = useRef(false)

  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  // Used to resolve a notification deep-link's `patientId` to the full
  // Patient record needed by `setSelectedPatient`. Empty array fallback
  // mirrors the Notes page so the lookup is a no-op until patients land.
  const patientList = useAppSelector((state) => state.patient.data ?? [])
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const createPerioChart = useAppSelector(
    (state) => state.charting.createPerioChart,
  )
  const createDentalChart = useAppSelector(
    (state) => state.charting.createDentalChart,
  )
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const dentalCharts = useAppSelector(
    (state) => state.charting.dentalCharts ?? [],
  )
  const perioCharts = useAppSelector(
    (state) => state.charting.perioCharts ?? [],
  )
  const treatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const isDentalUpdated = useAppSelector(
    (state) => state.charting.isDentalUpdated,
  )
  const isPerioUpdated = useAppSelector(
    (state) => state.charting.isPerioUpdated,
  )
  const {
    chartingSystem,
    isReady: chartingPreferencesReady,
    autoMarkCompletedWhenCharted,
  } = useClinicChartingPreferences()
  const {
    activeDentalChart,
    commitActiveDentalChartUpdate,
    isExplicitHistorySelection: isExplicitDentalHistorySelection,
  } = useActiveDentalChart()
  const {
    activePerioChart,
    commitActivePerioChartUpdate,
    isExplicitHistorySelection,
  } = useActivePerioChart()

  const chartingTab: ChartingRouteTab = useMemo(
    (): ChartingRouteTab => getChartingRouteTabFromPathname(location.pathname),
    [location.pathname],
  )

  const isPerioLocked = useAppSelector((state) => state.charting.isPerioLocked)
  const [dentalVoiceTooth, setDentalVoiceTooth] = useState<number | undefined>()
  const perioVoiceEnabled =
    chartingTab === 'perio' &&
    Boolean(selectedPatient?.patient_id) &&
    !isPerioLocked &&
    !isExplicitHistorySelection &&
    !chartingLoading
  const dentalVoiceEnabled =
    chartingTab === 'home' &&
    Boolean(selectedPatient?.patient_id) &&
    !isExplicitDentalHistorySelection &&
    !chartingLoading
  const voice = usePerioStreamingVoice({
    enabled: perioVoiceEnabled,
    contextKey: `${selectedClinic?.clinic_id}:${selectedPatient?.patient_id}:${activePerioChart.chart_id}:${createPerioChart.chart_id}`,
    chart: {
      ...activePerioChart,
      patient_id: selectedPatient?.patient_id ?? '',
      clinic_id: activePerioChart.clinic_id || selectedClinic?.clinic_id || '',
    },
    onChart: (next) => {
      commitActivePerioChartUpdate(next)
      dispatch(setPerioUpdated(true))
    },
  })
  const dentalVoice = useDentalStreamingVoice({
    enabled: dentalVoiceEnabled,
    contextKey: `${selectedClinic?.clinic_id}:${selectedPatient?.patient_id}:${activeDentalChart.chart_id}:${createDentalChart.chart_id}`,
    chart: {
      ...activeDentalChart,
      patient_id: selectedPatient?.patient_id ?? '',
      clinic_id: activeDentalChart.clinic_id || selectedClinic?.clinic_id || '',
    },
    onChart: (next) => {
      commitActiveDentalChartUpdate(next)
      dispatch(setDentalUpdated(true))
    },
    onToothChange: (tooth) => {
      setDentalVoiceTooth(tooth)
    },
  })
  const selectPerioVoiceCursor = voice.selectCursor
  const selectDentalVoiceCursor = dentalVoice.selectCursor
  const handleDentalSelectedToothChange = useCallback(
    (tooth: number | null): void => {
      if (tooth != null) selectDentalVoiceCursor({ tooth })
    },
    [selectDentalVoiceCursor],
  )
  const activeVoicePhase =
    chartingTab === 'home' ? dentalVoice.phase : voice.phase
  const isMicOn = ['connecting', 'listening', 'processing'].includes(
    activeVoicePhase,
  )
  const voiceEnabled =
    chartingTab === 'home'
      ? dentalVoiceEnabled
      : chartingTab === 'perio'
        ? perioVoiceEnabled
        : false
  const activeVoiceToggle = useMemo((): (() => void) => {
    if (chartingTab === 'home') return dentalVoice.toggle
    return voice.toggle
  }, [chartingTab, dentalVoice.toggle, voice.toggle])
  const activeMicStatus = activeVoicePhase
  const dentalVoiceActive = ['connecting', 'listening', 'processing'].includes(
    dentalVoice.phase,
  )
  const perioVoiceActive = ['connecting', 'listening', 'processing'].includes(
    voice.phase,
  )
  const [forceDentalVoiceAccordion, setForceDentalVoiceAccordion] =
    useState(false)
  const [forcePerioVoiceAccordion, setForcePerioVoiceAccordion] =
    useState(false)
  const wasDentalVoiceActiveRef = useRef(false)
  const wasPerioVoiceActiveRef = useRef(false)
  const handleMicToggle = useCallback((): void => {
    if (!isMicOn) {
      if (chartingTab === 'home') {
        flushSync(() => {
          setForceDentalVoiceAccordion(true)
        })
      } else if (chartingTab === 'perio') {
        flushSync(() => {
          setForcePerioVoiceAccordion(true)
        })
      }
    } else if (chartingTab === 'home') {
      setForceDentalVoiceAccordion(false)
    } else if (chartingTab === 'perio') {
      setForcePerioVoiceAccordion(false)
    }
    activeVoiceToggle()
  }, [activeVoiceToggle, chartingTab, isMicOn])
  useEffect(() => {
    if (dentalVoiceActive) {
      wasDentalVoiceActiveRef.current = true
      setForceDentalVoiceAccordion(true)
      return
    }
    if (wasDentalVoiceActiveRef.current) {
      wasDentalVoiceActiveRef.current = false
      setForceDentalVoiceAccordion(false)
    }
  }, [dentalVoiceActive])
  useEffect(() => {
    if (perioVoiceActive) {
      wasPerioVoiceActiveRef.current = true
      setForcePerioVoiceAccordion(true)
      return
    }
    if (wasPerioVoiceActiveRef.current) {
      wasPerioVoiceActiveRef.current = false
      setForcePerioVoiceAccordion(false)
    }
  }, [perioVoiceActive])
  const [isFullscreen, setIsFullscreen] = useState(false)
  const isXrayPanelOpen = useAppSelector((state) => state.ui.isXrayPanelOpen)
  const [chartIndex, setChartIndex] = useState(0)
  useEffect(() => {
    if (chartingTab === 'perio') {
      const visibleTooth = voice.viewCursor?.tooth ?? voice.keyword.cursor.tooth
      setChartIndex(visibleTooth <= 16 ? 0 : 1)
    }
  }, [chartingTab, voice.keyword.cursor.tooth, voice.viewCursor?.tooth])
  // Snapshot any selected file(s) passed via route state from the Files page
  // (cloud-storage path, Phase 7.2.4). Captured once in the consolidated
  // route-state effect below and forwarded to ChartingMain via prop so a
  // refresh doesn't re-trigger the load.
  const [initialEmbeddedFiles, setInitialEmbeddedFiles] = useState<
    EmbeddedXraySelectedFile[] | null
  >(null)

  // Deep-link target captured from a notification's `location.state`
  // (`{ patientId, chartId, chartType }`). The patient is selected eagerly
  // below; the chart selection is deferred to the apply effect because
  // `dentalCharts` / `perioCharts` are only populated after
  // `fetchChartingbyPatientId` resolves. A ref keeps the target alive across
  // the navigate-replace that clears `location.state`.
  const pendingChartDeepLinkRef = useRef<{
    chartId: string
    chartType: 'dental' | 'perio'
  } | null>(null)

  // Single owner of /charting route-state consumption: dispatches the X-ray
  // panel open flag, captures any incoming selected file(s) and any
  // notification chart deep-link, then clears location.state so back/forward
  // and component re-mounts can't replay it.
  useEffect(() => {
    const routeState = location.state as {
      xrayPanelOpen?: boolean
      selectedFile?: EmbeddedXraySelectedFile
      selectedFiles?: EmbeddedXraySelectedFile[]
      // Chart notification deep-link (see charting-notifications.service.ts).
      patientId?: string
      chartId?: string
      chartType?: 'dental' | 'perio'
    } | null
    if (!routeState) return
    const hasFiles =
      (routeState.selectedFiles && routeState.selectedFiles.length > 0) ||
      Boolean(routeState.selectedFile)
    const hasChartDeepLink = Boolean(routeState.chartId && routeState.chartType)
    if (!routeState.xrayPanelOpen && !hasFiles && !hasChartDeepLink) return

    if (routeState.xrayPanelOpen === true) {
      dispatch(setXrayPanelOpen(true))
    }
    if (hasFiles) {
      const next =
        routeState.selectedFiles && routeState.selectedFiles.length > 0
          ? routeState.selectedFiles
          : routeState.selectedFile
            ? [routeState.selectedFile]
            : null
      setInitialEmbeddedFiles(next)
    }

    if (hasChartDeepLink) {
      // Stash the chart target before clearing location.state. We can't apply
      // it yet â€” the relevant charts array won't exist until the patient
      // switch (below) triggers `fetchChartingbyPatientId`.
      pendingChartDeepLinkRef.current = {
        chartId: routeState.chartId as string,
        chartType: routeState.chartType as 'dental' | 'perio',
      }
      // Switch patient now if the notification targets a different one and
      // we have it in the Redux patient list. If the list hasn't loaded yet
      // (cold open), the apply effect below will short-circuit until charts
      // exist, and the existing patient-fetch effect on `selectedPatient`
      // will run as soon as the dispatch lands.
      if (
        routeState.patientId &&
        routeState.patientId !== selectedPatient?.patient_id
      ) {
        const target = patientList.find(
          (p) => p.patient_id === routeState.patientId,
        )
        if (target) {
          dispatch(setSelectedPatient({ patient: target }))
        }
      }
    }

    navigate(location.pathname, { replace: true, state: null })
  }, [
    dispatch,
    location.state,
    location.pathname,
    navigate,
    patientList,
    selectedPatient?.patient_id,
  ])

  // When the active patient is removed (e.g. via the header "Remove" action,
  // which nulls `selectedPatient`), drop any previously-loaded X-ray file(s)
  // so the embedded viewer returns to its empty state instead of holding on
  // to the prior patient's DICOM.
  useEffect(() => {
    if (!selectedPatient?.patient_id) {
      setInitialEmbeddedFiles(null)
    }
  }, [selectedPatient?.patient_id])

  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const [pendingSaveData, setPendingSaveData] =
    useState<PendingSaveConfirmation | null>(null)
  const [perioValidationErrors, setPerioValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [dentalValidationErrors, setDentalValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [dentalChartingInfoFocusTick, setDentalChartingInfoFocusTick] =
    useState(0)
  const [perioChartingInfoFocusTick, setPerioChartingInfoFocusTick] =
    useState(0)
  const [alertMessageInfo, setAlertMessageInfo] = useState<{
    title: string
    message: string
    type: 'success' | 'error'
    show: boolean
  }>({
    title: '',
    message: '',
    type: 'success',
    show: false,
  })
  const { openNoteEditor } = useNoteEditorOverlay()
  const prevClinicIdRef = useRef<string | undefined>(undefined)

  const locationState = location.state as {
    previousPage?: { path: string }
  } | null
  const pagePreviousState =
    locationState?.previousPage &&
    typeof locationState.previousPage.path === 'string'
      ? locationState.previousPage
      : null
  const addTreatmentPlanRef = useRef<(() => void) | null>(null)
  const [disableAddTreatmentPlan, setDisableAddTreatmentPlan] = useState(false)
  const treatmentPlanNavigationRef =
    useRef<TreatmentPlanNavigationHandle | null>(null)
  const [showUnsavedLeaveConfirm, setShowUnsavedLeaveConfirm] = useState(false)
  const [pendingLeave, setPendingLeave] = useState<PendingChartingLeave | null>(
    null,
  )
  const prevChartingTabRef = useRef<ChartingRouteTab | null>(null)
  const prevBootstrapPatientIdRef = useRef<string | null>(null)
  const prevBootstrapClinicIdRef = useRef<string | undefined>(undefined)
  const [isPageReady, setIsPageReady] = useState(false)
  const isPageReadyRef = useRef(false)
  const [dentalChartAssetsReady, setDentalChartAssetsReady] = useState(false)
  const [tabTransitionReady, setTabTransitionReady] = useState(true)
  // Keep dental mounted on dental/perio/history; unmount only on plan tab so treatment
  // plan ToothChart is the only live dental chart instance (shared SVG ids).
  const [dentalChartMounted, setDentalChartMounted] = useState(
    (): boolean =>
      getChartingRouteTabFromPathname(location.pathname) !== 'treatment-plan',
  )

  const isPatientBootstrapPending =
    Boolean(selectedPatient?.patient_id) &&
    (chartingLoading || !dentalChartAssetsReady)

  const isTabTransitionPending =
    Boolean(selectedPatient?.patient_id) && !tabTransitionReady

  const isChartContentPending =
    isPatientBootstrapPending || isTabTransitionPending

  const showTabSkeleton = isTabTransitionPending && !isPatientBootstrapPending

  useEffect(() => {
    if (!tabTransitionReady) {
      return
    }
    setDentalChartMounted(chartingTab !== 'treatment-plan')
  }, [chartingTab, tabTransitionReady])

  // On clinic change: show global loading, fetch patients for new clinic, then show Select Patient modal
  const clinicId = selectedClinic?.clinic_id
  useEffect(() => {
    const resolvedClinicId =
      clinicId ||
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('clinic_id') || ''
        : '')
    if (resolvedClinicId) {
      void prefetchClinicChartingPreferences(resolvedClinicId)
    }
  }, [clinicId])

  useEffect(() => {
    const prevClinicId = prevClinicIdRef.current
    if (prevClinicId !== undefined && prevClinicId !== clinicId) {
      showLoading({ container: GLOBAL_LOADING_CONTAINER })
      dispatch(fetchAllPatients())
        .unwrap()
        .finally(() => {
          hideLoading(GLOBAL_LOADING_CONTAINER)
        })
    }
    prevClinicIdRef.current = clinicId
  }, [clinicId, dispatch, showLoading, hideLoading])

  useLayoutEffect(() => {
    showLoading({
      container: CHARTING_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
  }, [showLoading])

  useEffect(() => {
    let cancelled = false
    isMountedRef.current = true
    const startedAt = Date.now()

    const patientId = selectedPatient?.patient_id ?? ''
    const bootstrapClinicId = selectedClinic?.clinic_id ?? ''
    const previousPatientId = prevBootstrapPatientIdRef.current
    const previousClinicId = prevBootstrapClinicIdRef.current
    const patientChanged = previousPatientId !== patientId
    const clinicChanged = previousClinicId !== bootstrapClinicId
    prevBootstrapPatientIdRef.current = patientId
    prevBootstrapClinicIdRef.current = bootstrapClinicId

    const finishOverlay = async (): Promise<void> => {
      await waitAtLeast(startedAt, FIRST_LOAD_OVERLAY_MS)
      if (cancelled || !isMountedRef.current) return
      isPageReadyRef.current = true
      setIsPageReady(true)
      hideLoading(CHARTING_LOADING_CONTAINER)
    }

    if (selectedPatient) {
      if (selectedDentalChart && selectedDentalChart.patient_id !== patientId) {
        dispatch(setSelectedDentalChart(null))
      }
      if (selectedPerioChart && selectedPerioChart.patient_id !== patientId) {
        dispatch(setSelectedPerioChart(null))
      }
    }

    if (patientId && (patientChanged || !isPageReadyRef.current)) {
      isPageReadyRef.current = false
      setIsPageReady(false)
      setDentalChartAssetsReady(false)
      setTabTransitionReady(false)
      showLoading({
        container: CHARTING_LOADING_CONTAINER,
        style: FIRST_LOAD_OVERLAY_STYLE,
      })
      Promise.all([
        dispatch(fetchChartingbyPatientId(patientId)).unwrap(),
        dispatch(fetchInsurancePlansByPatientId(patientId)).unwrap(),
        preloadChartingTabAssets(chartingTab),
      ]).finally(() => {
        void (async (): Promise<void> => {
          if (cancelled || !isMountedRef.current) return
          await finishOverlay()
          if (cancelled || !isMountedRef.current) return
          setDentalChartAssetsReady(true)
          setTabTransitionReady(true)
          prevChartingTabRef.current = chartingTab
        })()
      })
      setPerioValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors }
        delete updatedErrors['patient_id']
        return updatedErrors
      })
      setDentalValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors }
        delete updatedErrors['patient_id']
        return updatedErrors
      })
      dispatch(
        setCreatePerioChart({
          ...EmptyPerioChart,
          patient_id: patientId,
          clinic_id: bootstrapClinicId,
        }),
      )
      dispatch(
        setCreateDentalChart({
          ...EmptyDentalChart,
          patient_id: patientId,
          clinic_id: bootstrapClinicId,
        }),
      )
      dispatch(setPreferCreateDentalChart(false))
      dispatch(setPreferCreatePerioChart(false))
      dispatch(setDentalUpdated(false))
      dispatch(setPerioUpdated(false))
      dispatch(setCompareCharts(null))
      setPerioValidationErrors({})
      setDentalValidationErrors({})
    } else if (!patientId && (patientChanged || !isPageReadyRef.current)) {
      if (patientChanged) {
        isPageReadyRef.current = false
        setIsPageReady(false)
        setDentalChartAssetsReady(false)
        setTabTransitionReady(true)
        prevChartingTabRef.current = null
        dispatch(clearAllChartingData())
        if (isMountedRef.current && gridRef.current) {
          try {
            const gridElement = gridRef.current.element
            if (gridElement && gridElement.parentNode) {
              gridRef.current.dataSource = []
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error clearing grid data source:', error)
          }
        }
      }
      showLoading({
        container: CHARTING_LOADING_CONTAINER,
        style: FIRST_LOAD_OVERLAY_STYLE,
      })
      void finishOverlay()
    } else if (
      patientId &&
      clinicChanged &&
      !patientChanged &&
      bootstrapClinicId
    ) {
      if (createPerioChart.clinic_id !== bootstrapClinicId) {
        dispatch(
          setCreatePerioChart({
            ...createPerioChart,
            clinic_id: bootstrapClinicId,
          }),
        )
      }
      if (createDentalChart.clinic_id !== bootstrapClinicId) {
        dispatch(
          setCreateDentalChart({
            ...createDentalChart,
            clinic_id: bootstrapClinicId,
          }),
        )
      }
    }

    return (): void => {
      cancelled = true
      isMountedRef.current = false
    }
    // chartingTab: preload at patient select only; tab switches use the effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap only when patient/clinic id changes
  }, [
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
    dispatch,
    showLoading,
    hideLoading,
  ])

  useEffect(() => {
    return () => {
      hideLoading(CHARTING_LOADING_CONTAINER)
    }
  }, [hideLoading])

  // Apply a pending notification chart deep-link once the relevant charts
  // array has been populated for the current patient. Runs at most once per
  // captured target â€” the ref is cleared after dispatching the setter (or
  // after we determine the target chart doesn't exist for this patient).
  // Idempotent: re-runs are guarded by `pendingChartDeepLinkRef.current`.
  useEffect(() => {
    const target = pendingChartDeepLinkRef.current
    if (!target) return
    if (!selectedPatient?.patient_id) return
    // Wait until the patient-bootstrap fetch completes so charts arrays are
    // authoritative for this patient.
    if (isPatientBootstrapPending) return

    if (target.chartType === 'dental') {
      const match = dentalCharts.find((c) => c.chart_id === target.chartId)
      pendingChartDeepLinkRef.current = null
      if (match && match.patient_id === selectedPatient.patient_id) {
        dispatch(setSelectedDentalChart(match))
      }
    } else {
      // The perio notification's actionUrl is `/charting/perio`, which renders
      // `PerioPage` directly off `selectedPerioChart` â€” no layer toggle is
      // needed here (that's only for the dental "home" tab's perio overlay).
      const match = perioCharts.find((c) => c.chart_id === target.chartId)
      pendingChartDeepLinkRef.current = null
      if (match && match.patient_id === selectedPatient.patient_id) {
        dispatch(setSelectedPerioChart(match))
      }
    }
  }, [
    dispatch,
    selectedPatient?.patient_id,
    isPatientBootstrapPending,
    dentalCharts,
    perioCharts,
  ])

  const ensureSectionData = (
    data?: PerioChartSectionData,
  ): PerioChartSectionData => {
    const empty = createEmptyPerioChartData().facial
    return {
      PD: data?.PD ?? empty.PD,
      GM: data?.GM ?? empty.GM,
      CAL: data?.CAL ?? empty.CAL,
      Bleeding: data?.Bleeding ?? empty.Bleeding,
      Calculus: data?.Calculus ?? empty.Calculus,
      Supp: data?.Supp ?? empty.Supp,
      Furcation: data?.Furcation ?? empty.Furcation,
      Mobility: data?.Mobility ?? empty.Mobility,
    }
  }

  useEffect(() => {
    const patientId = selectedPatient?.patient_id
    if (!patientId) {
      return
    }

    if (isPatientBootstrapPending) {
      return
    }

    const previousTab = prevChartingTabRef.current
    if (previousTab === chartingTab) {
      return
    }

    let cancelled = false
    setTabTransitionReady(false)

    void preloadChartingTabAssets(chartingTab).finally(() => {
      if (cancelled || !isMountedRef.current) {
        return
      }
      prevChartingTabRef.current = chartingTab
      void Promise.all([
        waitForChartingTabPaint(),
        waitMs(TAB_SKELETON_DURATION_MS),
      ]).then(() => {
        if (!cancelled && isMountedRef.current) {
          setTabTransitionReady(true)
        }
      })
    })

    return (): void => {
      cancelled = true
    }
  }, [chartingTab, selectedPatient?.patient_id, isPatientBootstrapPending])

  const [layers, setLayers] = useState<ChartingLayersState>(() =>
    defaultChartingViewToLayers(undefined),
  )
  const layersInitializedRef = useRef(false)
  const layersClinicIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!chartingPreferencesReady) {
      return
    }

    const shouldApplyDefaults =
      !layersInitializedRef.current || layersClinicIdRef.current !== clinicId

    if (!shouldApplyDefaults) {
      return
    }

    setLayers(defaultChartingViewToLayers(chartingSystem.default_charting_view))
    layersInitializedRef.current = true
    layersClinicIdRef.current = clinicId
  }, [chartingPreferencesReady, chartingSystem.default_charting_view, clinicId])

  const handleLayerChange = (layer: ChartingLayerKey): void => {
    setLayers((prev) => ({
      ...prev,
      [layer]: !prev[layer],
    }))
  }

  const handleChartToggle = (direction: 'left' | 'right'): void => {
    setChartIndex(direction === 'left' ? 0 : 1)
    const isUpperArch = voice.keyword.cursor.tooth <= 16
    if (chartingTab === 'perio' && isUpperArch !== (direction === 'left'))
      voice.selectCursor({ tooth: direction === 'left' ? 1 : 17 })
  }

  const hasTreatmentPlanUnsaved = useCallback((): boolean => {
    return treatmentPlanNavigationRef.current?.hasUnsavedChanges() === true
  }, [])

  const hasAnyUnsavedForLeaveGuard = useCallback((): boolean => {
    if (hasTreatmentPlanUnsaved()) {
      return true
    }
    if (chartingTab === 'home') {
      return isDentalUpdated
    }
    if (chartingTab === 'perio') {
      return isPerioUpdated
    }
    return false
  }, [chartingTab, hasTreatmentPlanUnsaved, isDentalUpdated, isPerioUpdated])

  // Tab switches stay in charting; dental/perio/treatment plan stay mounted (hidden) so
  // accordion overlays and ToothChart do not thrash on dentalâ†”perio switches. Dental
  // unmounts only on the plan tab (duplicate ToothChart ids). Unsaved dental/perio on
  // the active tab block tab changesâ€”not background TP edits.
  const shouldConfirmChartingTabChange = useCallback(
    (targetTab: string): boolean => {
      if (targetTab === chartingTab) {
        return false
      }
      if (chartingTab === 'home' && isDentalUpdated) {
        return true
      }
      if (chartingTab === 'perio' && isPerioUpdated) {
        return true
      }
      return false
    },
    [chartingTab, isDentalUpdated, isPerioUpdated],
  )

  useChartingLeaveClickGuard(
    () => hasAnyUnsavedForLeaveGuard(),
    (targetPath) => {
      if (!autoSaveEnabledRef.current) {
        setPendingLeave({ kind: 'route', path: targetPath, state: null })
        setShowUnsavedLeaveConfirm(true)
        return
      }
      void tryAutoSaveBeforePromptRef.current().then((saved) => {
        if (saved) {
          navigate(targetPath)
          return
        }
        setPendingLeave({ kind: 'route', path: targetPath, state: null })
        setShowUnsavedLeaveConfirm(true)
      })
    },
  )
  useChartingBeforeUnload(() => hasAnyUnsavedForLeaveGuard())

  const performTabNavigation = useCallback(
    (tab: string): void => {
      if (
        tab === 'home' ||
        tab === 'perio' ||
        tab === 'treatment-plan' ||
        tab === 'history'
      ) {
        if (selectedPatient?.patient_id && tab !== chartingTab) {
          flushSync(() => {
            setTabTransitionReady(false)
          })
        }
        navigate(tab === 'home' ? '/charting' : `/charting/${tab}`)
      }
    },
    [navigate, selectedPatient?.patient_id, chartingTab],
  )

  const handleChangeTab = useCallback(
    (tab: string): void => {
      if (shouldConfirmChartingTabChange(tab)) {
        if (!autoSaveEnabledRef.current) {
          setPendingLeave({ kind: 'charting-tab', tab })
          setShowUnsavedLeaveConfirm(true)
          return
        }
        void tryAutoSaveBeforePromptRef.current().then((saved) => {
          if (saved) {
            performTabNavigation(tab)
            return
          }
          setPendingLeave({ kind: 'charting-tab', tab })
          setShowUnsavedLeaveConfirm(true)
        })
        return
      }
      performTabNavigation(tab)
    },
    [shouldConfirmChartingTabChange, performTabNavigation],
  )

  const handleGoToPlans = useCallback((): void => {
    handleChangeTab('treatment-plan')
  }, [handleChangeTab])

  const completePendingLeave = useCallback((): void => {
    const pending = pendingLeave
    setPendingLeave(null)
    setShowUnsavedLeaveConfirm(false)
    if (!pending) {
      return
    }
    if (pending.kind === 'charting-tab') {
      performTabNavigation(pending.tab)
      return
    }
    navigate(pending.path, pending.state ? { state: pending.state } : undefined)
  }, [pendingLeave, performTabNavigation, navigate])

  const requestLeavePage = useCallback(
    (targetPath: string, state?: Record<string, unknown>): void => {
      if (!hasAnyUnsavedForLeaveGuard()) {
        navigate(targetPath, state ? { state } : undefined)
        return
      }
      if (!autoSaveEnabledRef.current) {
        setPendingLeave({
          kind: 'route',
          path: targetPath,
          state: state ?? null,
        })
        setShowUnsavedLeaveConfirm(true)
        return
      }
      void tryAutoSaveBeforePromptRef.current().then((saved) => {
        if (saved) {
          navigate(targetPath, state ? { state } : undefined)
          return
        }
        setPendingLeave({
          kind: 'route',
          path: targetPath,
          state: state ?? null,
        })
        setShowUnsavedLeaveConfirm(true)
      })
    },
    [hasAnyUnsavedForLeaveGuard, navigate],
  )

  const handleGoBack = useCallback((): void => {
    if (!pagePreviousState) {
      return
    }
    requestLeavePage(pagePreviousState.path, { ...pagePreviousState })
  }, [pagePreviousState, requestLeavePage])

  const handleOpenChartingNotesOverlay = (
    id: string | null,
    openFirstNote?: boolean,
  ): void => {
    if (!selectedPatient) {
      showToast(createFailedToast('Notes', 'Please select a patient first'))
      return
    }
    const payload = resolveChartingNoteOverlayPayload({
      currentTab: chartingTab,
      id,
      openFirstNote,
      treatmentPlans,
      dentalCharts,
      perioCharts,
      selectedDentalChart,
      createDentalChart,
      activeDentalChart,
      selectedPerioChart,
      createPerioChart,
    })
    openNoteEditor({
      patient: selectedPatient,
      contextId: payload.contextId ?? null,
      contextType: payload.contextType ?? null,
      contextName: payload.contextName ?? null,
      openFirstNote: payload.openFirstNote ?? false,
      editorBackButtonLabel: chartingNotesBackLabel,
      openContextNotesByDefault: true,
      overlayBackLabel: chartingNotesBackLabel,
      initialView: payload.openFirstNote ? 'list' : 'add',
      anchorClassName: 'charting-note-editor-overlay-anchor',
      notesFetchLoadingContainer: 'body',
      portalTarget: '.page-content',
      pagePreviousState,
    })
  }

  const updatePerioValue = (
    section: PerioSection,
    label: keyof PerioChartSectionData,
    toothIndex: number,
    newValue: string,
  ): void => {
    const facial = ensureSectionData(activePerioChart?.perio_data?.facial)
    const lingual = ensureSectionData(activePerioChart?.perio_data?.lingual)
    const updatedSection = section === PerioSection.Facial ? facial : lingual
    const updatedValues = [...updatedSection[label]]
    updatedValues[toothIndex] = newValue

    const updatedData: PerioChartSectionData = {
      ...updatedSection,
      [label]: updatedValues,
    }
    const chartNewValue = {
      ...activePerioChart,
      chart_id: activePerioChart?.chart_id ?? '',
      provider_id: activePerioChart?.provider_id ?? '',
      patient_id: activePerioChart?.patient_id ?? '',
      title: activePerioChart?.title ?? '',
      description: activePerioChart?.description ?? '',
      perio_data: {
        facial: section === PerioSection.Facial ? updatedData : facial,
        lingual: section === PerioSection.Lingual ? updatedData : lingual,
      },
    }
    commitActivePerioChartUpdate(chartNewValue)
    dispatch(setPerioUpdated(true))
  }

  const clearSelection = (): void => {
    if (isMountedRef.current && gridRef.current) {
      try {
        const gridElement = gridRef.current.element
        if (gridElement && gridElement.parentNode) {
          clearChartHistorySelectionClasses(gridRef.current)
          gridRef.current.clearSelection()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Error clearing grid selection:', error)
      }
    }
  }
  const handleAddChart = (): void => {
    if (chartingTab === 'perio')
      selectPerioVoiceCursor({
        tooth: 1,
        section: PerioSection.Facial,
        metric: 'PD',
      })
    // Data consistency validation: ensure patient and clinic match
    if (selectedPatient && selectedClinic) {
      if (selectedPatient.clinic_id !== selectedClinic.clinic_id) {
        setAlertMessageInfo({
          title: 'Clinic Mismatch',
          message:
            'The selected patient does not belong to the selected clinic. Please select a patient from the current clinic.',
          type: 'error',
          show: true,
        })
        return
      }
    }

    if (chartingTab === 'perio') {
      setPerioValidationErrors({})
      dispatch(setSelectedPerioChart(null))
      dispatch(setPreferCreatePerioChart(true))
      dispatch(
        setCreatePerioChart({
          ...EmptyPerioChart,
          patient_id: selectedPatient?.patient_id ?? '',
          clinic_id: selectedClinic?.clinic_id ?? '',
        }),
      )
    } else if (chartingTab === 'home') {
      setDentalValidationErrors({})
      dispatch(setSelectedDentalChart(null))
      dispatch(setPreferCreateDentalChart(true))
      dispatch(
        setCreateDentalChart({
          ...EmptyDentalChart,
          patient_id: selectedPatient?.patient_id ?? '',
          clinic_id: selectedClinic?.clinic_id ?? '',
        }),
      )
    }
    dispatch(setCompareCharts(null))
    dispatch(setDentalUpdated(false))
    dispatch(setPerioUpdated(false))
    clearSelection()
  }

  const handleResetChart = useCallback((): void => {
    if (chartingTab === 'perio')
      selectPerioVoiceCursor({
        tooth: 1,
        section: PerioSection.Facial,
        metric: 'PD',
      })
    const patientId = selectedPatient?.patient_id ?? ''
    const resetClinicId = selectedClinic?.clinic_id ?? ''
    if (chartingTab === 'perio') {
      setPerioValidationErrors({})
      const isPerioEdit = isAlignedPersistedPerioEditSession(
        selectedPerioChart,
        createPerioChart,
      )
      if (isPerioEdit) {
        const chartId = selectedPerioChart?.chart_id?.trim() ?? ''
        const restored =
          perioCharts.find((chart) => chart.chart_id === chartId) ??
          selectedPerioChart
        flushSync(() => {
          dispatch(setSelectedPerioChart(restored))
          dispatch(
            setCreatePerioChart({
              ...EmptyPerioChart,
              patient_id: patientId,
              clinic_id: resetClinicId,
            }),
          )
          dispatch(setPreferCreatePerioChart(false))
        })
        dispatch(setPerioUpdated(false))
        return
      }
      flushSync(() => {
        dispatch(resetPerioChart())
      })
      dispatch(setPerioUpdated(false))
    }
    if (chartingTab === 'home') {
      setDentalValidationErrors({})
      const isDentalEdit = isAlignedPersistedDentalEditSession(
        selectedDentalChart,
        createDentalChart,
      )
      if (isDentalEdit) {
        const chartId = selectedDentalChart?.chart_id?.trim() ?? ''
        const restored =
          dentalCharts.find((chart) => chart.chart_id === chartId) ??
          selectedDentalChart
        flushSync(() => {
          dispatch(setSelectedDentalChart(restored))
          dispatch(
            setCreateDentalChart({
              ...EmptyDentalChart,
              patient_id: patientId,
              clinic_id: resetClinicId,
            }),
          )
          dispatch(setPreferCreateDentalChart(false))
        })
        dispatch(setDentalUpdated(false))
        return
      }
      flushSync(() => {
        dispatch(resetDentalChart())
      })
      dispatch(setDentalUpdated(false))
    }
    clearSelection()
  }, [
    chartingTab,
    createDentalChart,
    createPerioChart,
    dentalCharts,
    dispatch,
    perioCharts,
    selectedClinic?.clinic_id,
    selectedDentalChart,
    selectedPatient?.patient_id,
    selectedPerioChart,
    selectPerioVoiceCursor,
  ])

  const validatePerioFormForManualSave = useCallback((): boolean => {
    const isNewChart = !activePerioChart?.chart_id?.trim()
    const errors: { [key: string]: string } = {}

    if (perioValidationErrors.provider_id === CHART_PROVIDER_INVALID_ERROR) {
      errors.provider_id = CHART_PROVIDER_INVALID_ERROR
    }

    if (!activePerioChart?.patient_id && !selectedPatient?.patient_id) {
      errors.patient_id = CHART_PATIENT_REQUIRED_ERROR
    }

    if (isNewChart) {
      if (!activePerioChart?.provider_id) {
        errors.provider_id = CHART_PROVIDER_REQUIRED_ERROR
      }
      if (!activePerioChart?.title?.trim()) {
        errors.title = CHART_TITLE_REQUIRED_ERROR
      }
    }

    if (!hasPerioChartableContent(activePerioChart?.perio_data)) {
      errors.content = CHART_PERIO_CONTENT_REQUIRED_ERROR
    }

    setPerioValidationErrors(errors)
    const hasErrors = Object.keys(errors).length > 0
    if (hasErrors) {
      if (hasChartingInformationFieldErrors(errors)) {
        setPerioChartingInfoFocusTick((tick) => tick + 1)
      }
      showToast(
        createFailedToast(
          'Cannot save chart',
          buildManualSaveValidationToastMessage(errors),
        ),
      )
    }
    return !hasErrors
  }, [
    activePerioChart,
    perioValidationErrors.provider_id,
    selectedPatient?.patient_id,
    showToast,
  ])

  const validateDentalFormForManualSave = useCallback((): boolean => {
    const chartToValidate = activeDentalChart
    const isNewChart = !chartToValidate?.chart_id?.trim()
    const errors: { [key: string]: string } = {}

    if (dentalValidationErrors.provider_id === CHART_PROVIDER_INVALID_ERROR) {
      errors.provider_id = CHART_PROVIDER_INVALID_ERROR
    }

    if (!chartToValidate?.patient_id && !selectedPatient?.patient_id) {
      errors.patient_id = CHART_PATIENT_REQUIRED_ERROR
    }

    if (isNewChart) {
      if (!chartToValidate?.provider_id) {
        errors.provider_id = CHART_PROVIDER_REQUIRED_ERROR
      }
      if (!chartToValidate?.title?.trim()) {
        errors.title = CHART_TITLE_REQUIRED_ERROR
      }
    }

    if (!hasDentalChartableContent(chartToValidate?.dental_data)) {
      errors.content = CHART_DENTAL_CONTENT_REQUIRED_ERROR
    }

    setDentalValidationErrors(errors)
    const hasErrors = Object.keys(errors).length > 0
    if (hasErrors) {
      if (hasChartingInformationFieldErrors(errors)) {
        setDentalChartingInfoFocusTick((tick) => tick + 1)
      }
      showToast(
        createFailedToast(
          'Cannot save chart',
          buildManualSaveValidationToastMessage(errors),
        ),
      )
    }
    return !hasErrors
  }, [
    activeDentalChart,
    dentalValidationErrors.provider_id,
    selectedPatient?.patient_id,
    showToast,
  ])

  const validatePerioFormForAutoSave = useCallback((): boolean => {
    const errors: { [key: string]: string } = {}
    if (!createPerioChart?.patient_id && !selectedPatient?.patient_id) {
      errors.patient_id = CHART_PATIENT_REQUIRED_ERROR
    }
    setPerioValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [createPerioChart?.patient_id, selectedPatient?.patient_id])

  const validateDentalFormForAutoSave = useCallback((): boolean => {
    const errors: { [key: string]: string } = {}
    if (!activeDentalChart?.patient_id && !selectedPatient?.patient_id) {
      errors.patient_id = CHART_PATIENT_REQUIRED_ERROR
    }
    setDentalValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [activeDentalChart?.patient_id, selectedPatient?.patient_id])

  const validateForManualSave = useCallback(
    (tab: ChartingRouteTab): boolean => {
      if (tab === 'home') return validateDentalFormForManualSave()
      if (tab === 'perio') return validatePerioFormForManualSave()
      return false
    },
    [validateDentalFormForManualSave, validatePerioFormForManualSave],
  )

  const validateForAutoSave = useCallback(
    (tab: ChartingRouteTab): boolean => {
      if (tab === 'home') return validateDentalFormForAutoSave()
      if (tab === 'perio') return validatePerioFormForAutoSave()
      return false
    },
    [validateDentalFormForAutoSave, validatePerioFormForAutoSave],
  )

  const { syncTreatmentPlanCompletionAfterChartSave } =
    useTxPlanAutoCompleteOnChartSave({
      enabled: autoMarkCompletedWhenCharted,
      patientId: selectedPatient?.patient_id ?? '',
      treatmentPlans,
    })

  const {
    persistChart,
    exitEditingSession,
    dentalSignature,
    perioSignature,
    canAutoSaveDental,
    canAutoSavePerio,
    resolveDentalChartForSave,
    resolvePerioChartForSave,
  } = useChartPersist({
    validateForManualSave,
    validateForAutoSave,
    syncTreatmentPlanCompletionAfterChartSave,
  })

  const buildSaveConfirmation = useCallback(
    (type: 'dental' | 'perio'): PendingSaveConfirmation => {
      if (type === 'perio') {
        const chart = resolvePerioChartForSave()
        const chartId = chart.chart_id ?? ''
        const patientId =
          chart.patient_id !== ''
            ? chart.patient_id
            : (selectedPatient?.patient_id ?? '')
        return {
          type,
          chartId,
          titleResolution: getUniqueChartTitleResolution(
            chart.title ?? '',
            perioCharts,
            patientId,
            chartId,
          ),
        }
      }

      const chart = resolveDentalChartForSave()
      const chartId = chart.chart_id ?? ''
      const patientId =
        chart.patient_id !== ''
          ? chart.patient_id
          : (selectedPatient?.patient_id ?? '')
      return {
        type,
        chartId,
        titleResolution: getUniqueChartTitleResolution(
          chart.title ?? '',
          dentalCharts,
          patientId,
          chartId,
        ),
        dentalSummary: formatDentalDataSummary(chart.dental_data || []),
      }
    },
    [
      dentalCharts,
      perioCharts,
      resolveDentalChartForSave,
      resolvePerioChartForSave,
      selectedPatient?.patient_id,
    ],
  )

  const autoSaveEnabled =
    chartingPreferencesReady && chartingSystem.autosave_changes === 'yes'

  const isInPersistedEditSession = useMemo((): boolean => {
    if (chartingTab === 'home') {
      return isAlignedPersistedDentalEditSession(
        selectedDentalChart,
        createDentalChart,
      )
    }
    if (chartingTab === 'perio') {
      return isAlignedPersistedPerioEditSession(
        selectedPerioChart,
        createPerioChart,
      )
    }
    return false
  }, [
    chartingTab,
    createDentalChart,
    createPerioChart,
    selectedDentalChart,
    selectedPerioChart,
  ])

  const canFinishEditing = isInPersistedEditSession

  const handleAutoSave = useCallback(async (): Promise<boolean> => {
    return persistChart({
      mode: 'autosave',
      chartingTab,
      keepEditingSession: true,
      silent: true,
    })
  }, [chartingTab, persistChart])

  const { status: autoSaveStatus, triggerAutoSave } = useChartAutoSave({
    enabled: autoSaveEnabled,
    chartingTab,
    isDentalUpdated,
    isPerioUpdated,
    dentalSignature,
    perioSignature,
    canAutoSaveDental,
    canAutoSavePerio,
    onAutoSave: handleAutoSave,
  })

  const tryAutoSaveBeforePrompt = useCallback(async (): Promise<boolean> => {
    if (!autoSaveEnabled) {
      return false
    }
    const isUpdated =
      chartingTab === 'home'
        ? isDentalUpdated
        : chartingTab === 'perio'
          ? isPerioUpdated
          : false
    if (!isUpdated) {
      return true
    }
    const canSave =
      chartingTab === 'home' ? canAutoSaveDental : canAutoSavePerio
    if (!canSave) {
      return false
    }
    return triggerAutoSave()
  }, [
    autoSaveEnabled,
    canAutoSaveDental,
    canAutoSavePerio,
    chartingTab,
    isDentalUpdated,
    isPerioUpdated,
    triggerAutoSave,
  ])

  useEffect(() => {
    tryAutoSaveBeforePromptRef.current = tryAutoSaveBeforePrompt
    autoSaveEnabledRef.current = autoSaveEnabled
  }, [tryAutoSaveBeforePrompt, autoSaveEnabled])

  useEffect(() => {
    return () => {
      if (autoSaveEnabled) {
        void triggerAutoSave()
      }
    }
  }, [autoSaveEnabled, triggerAutoSave])

  const handleSaveChart = (): void => {
    const isUpdated = chartingTab === 'home' ? isDentalUpdated : isPerioUpdated

    if (canFinishEditing && !isUpdated) {
      exitEditingSession(chartingTab)
      return
    }

    if (chartingTab === 'home') {
      if (!validateDentalFormForManualSave()) return
      setPendingSaveData(buildSaveConfirmation('dental'))
    } else if (chartingTab === 'perio') {
      if (!validatePerioFormForManualSave()) return
      setPendingSaveData(buildSaveConfirmation('perio'))
    } else {
      return
    }

    setShowSaveConfirm(true)
  }

  const confirmSaveChart = async (): Promise<void> => {
    if (!pendingSaveData) return
    const chartingTabToSave =
      pendingSaveData.type === 'dental' ? 'home' : 'perio'
    setPendingSaveData(null)
    setShowSaveConfirm(false)
    await persistChart({
      mode: 'manual',
      chartingTab: chartingTabToSave,
      keepEditingSession: false,
      silent: false,
    })
  }

  const dismissSaveConfirm = (): void => {
    setShowSaveConfirm(false)
    setPendingSaveData(null)
  }

  const discardCurrentTabChanges = useCallback((): void => {
    if (hasTreatmentPlanUnsaved()) {
      treatmentPlanNavigationRef.current?.discardUnsavedChanges()
    }
    if (chartingTab === 'home' && isDentalUpdated) {
      handleResetChart()
    }
    if (chartingTab === 'perio' && isPerioUpdated) {
      handleResetChart()
    }
  }, [
    chartingTab,
    hasTreatmentPlanUnsaved,
    isDentalUpdated,
    isPerioUpdated,
    handleResetChart,
  ])

  const handleUnsavedLeaveCancel = (): void => {
    setShowUnsavedLeaveConfirm(false)
    setPendingLeave(null)
  }

  const handleUnsavedLeaveDiscard = (): void => {
    discardCurrentTabChanges()
    completePendingLeave()
  }

  return (
    <Container fluid className="charting-page p-2 h-100">
      <div
        className={`charting-loading-container charting-page-content${
          isPageReady ? ' is-ready' : ''
        }`}
      >
        {pagePreviousState && (
          <GoBackButton
            previousPageState={pagePreviousState}
            onBackClick={handleGoBack}
          />
        )}
        <ChartingTopBar
          currentTab={chartingTab}
          onTabChange={handleChangeTab}
          layers={layers}
          onLayerChange={handleLayerChange}
          chartIndex={chartIndex}
          onChartToggle={handleChartToggle}
          isMicOn={isMicOn}
          onMicToggle={handleMicToggle}
          micDisabled={!voiceEnabled}
          micStatus={activeMicStatus}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          onAddChart={handleAddChart}
          onSaveChart={handleSaveChart}
          onResetChart={handleResetChart}
          autoSaveEnabled={autoSaveEnabled}
          autoSaveStatus={autoSaveStatus}
          canFinishEditing={canFinishEditing}
          onViewNote={handleOpenChartingNotesOverlay}
          clearSelection={clearSelection}
          onAddTreatmentPlan={() => {
            if (addTreatmentPlanRef.current) {
              addTreatmentPlanRef.current()
            }
          }}
          disableAddTreatmentPlan={disableAddTreatmentPlan}
          isXrayPanelOpen={isXrayPanelOpen}
          onXrayPanelToggle={() => {
            // When closing the panel, drop any previously-loaded file(s) so
            // re-opening starts at the empty state (local/cloud chooser)
            // instead of auto-reloading the last DICOM.
            if (isXrayPanelOpen) {
              setInitialEmbeddedFiles(null)
            }
            dispatch(toggleXrayPanel())
          }}
        />

        <div className="charting-tab-stage">
          {showTabSkeleton ? <ChartingSkeleton variant={chartingTab} /> : null}
          <div
            className={`charting-tab-body flex-grow-1 min-h-0 d-flex flex-column${
              isChartContentPending ? ' charting-tab-body--pending' : ''
            }`}
          >
            {!selectedPatient?.patient_id ? (
              <div className="charting-no-patient-empty-state">
                <EmptyState
                  className="charting-no-patient-empty"
                  iconClass="mgc mgc_document_fill"
                  title="No charting data to display"
                  description="Select a patient to view their charts."
                  actionLabel="Select a patient"
                  onAction={() => dispatch(requestPatientSelection())}
                />
              </div>
            ) : (
              <>
                <Routes>
                  <Route path="/" element={null} />
                  <Route path="perio" element={null} />
                  <Route path="treatment-plan" element={null} />
                  <Route
                    path="history"
                    element={
                      <HistoryPage
                        onViewNote={handleOpenChartingNotesOverlay}
                        onGoToPlans={handleGoToPlans}
                      />
                    }
                  />
                </Routes>

                {dentalChartMounted ? (
                  <div
                    className={`charting-persistent-dental h-100${
                      chartingTab !== 'home'
                        ? ' charting-persistent-pane--inactive'
                        : ''
                    }`}
                    aria-hidden={chartingTab !== 'home'}
                  >
                    <ChartingMain
                      onViewNote={handleOpenChartingNotesOverlay}
                      layers={layers}
                      isFullscreen={isFullscreen}
                      isXrayPanelOpen={isXrayPanelOpen}
                      gridRef={gridRef}
                      validationErrors={dentalValidationErrors}
                      setValidationErrors={setDentalValidationErrors}
                      initialEmbeddedFiles={initialEmbeddedFiles}
                      chartBootstrapComplete={!isChartContentPending}
                      isHostVisible={
                        chartingTab === 'home' && !isChartContentPending
                      }
                      voiceStatus={<DentalVoiceStatus voice={dentalVoice} />}
                      voiceActive={
                        dentalVoiceActive || forceDentalVoiceAccordion
                      }
                      chartingInfoFocusTick={dentalChartingInfoFocusTick}
                      voiceTooth={dentalVoiceTooth}
                      onSelectedToothChange={handleDentalSelectedToothChange}
                    />
                  </div>
                ) : null}

                <div
                  className={`charting-persistent-perio h-100${
                    chartingTab !== 'perio'
                      ? ' charting-persistent-pane--inactive'
                      : ''
                  }`}
                  aria-hidden={chartingTab !== 'perio'}
                >
                  <PerioPage
                    voiceStatus={<PerioVoiceStatus voice={voice} />}
                    voiceActive={perioVoiceActive || forcePerioVoiceAccordion}
                    chartingInfoFocusTick={perioChartingInfoFocusTick}
                    voiceMeasurement={
                      <select
                        id="perio-voice-measurement"
                        aria-label="Voice measurement"
                        className="form-select form-select-sm input-container"
                        value={voice.keyword.cursor.metric}
                        disabled={!perioVoiceEnabled}
                        onChange={(event) =>
                          voice.selectCursor({
                            metric: event.target.value as
                              | 'PD'
                              | 'GM'
                              | 'CAL'
                              | 'Mobility',
                          })
                        }
                      >
                        <option value="PD">Pocket depth</option>
                        <option value="GM">Gingival margin</option>
                        <option value="CAL">Clinical attachment level</option>
                        <option value="Mobility">Mobility</option>
                      </select>
                    }
                    cursor={voice.keyword.cursor}
                    voiceHighlight={
                      isMicOn
                        ? (voice.viewCursor ?? voice.keyword.cursor)
                        : undefined
                    }
                    onCursor={voice.selectCursor}
                    chartIndex={chartIndex}
                    onViewNote={handleOpenChartingNotesOverlay}
                    updatePerioValue={updatePerioValue}
                    isFullscreen={isFullscreen}
                    gridRef={gridRef}
                    validationErrors={perioValidationErrors}
                    setValidationErrors={setPerioValidationErrors}
                  />
                </div>

                <div
                  className={`charting-persistent-treatment-plan h-100${
                    chartingTab !== 'treatment-plan'
                      ? ' charting-persistent-pane--inactive'
                      : ''
                  }${isChartContentPending ? ' charting-tab-body--pending' : ''}`}
                  aria-hidden={chartingTab !== 'treatment-plan'}
                >
                  <TreatmentPlanPage
                    onViewNote={handleOpenChartingNotesOverlay}
                    onAddTreatmentPlanRef={addTreatmentPlanRef}
                    onEmptyPlansChange={setDisableAddTreatmentPlan}
                    navigationRef={treatmentPlanNavigationRef}
                    isXrayPanelOpen={isXrayPanelOpen}
                    initialEmbeddedFiles={initialEmbeddedFiles}
                    isHostVisible={
                      chartingTab === 'treatment-plan' && !isChartContentPending
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <ConfirmationModal
          show={showSaveConfirm}
          onHide={dismissSaveConfirm}
          title="Confirmation"
          className="confirmation-modal--alert"
          headerClassName="confirmation-modal__header"
          size="md"
          body={
            <div className="confirmation-modal__body">
              <p className="confirmation-modal__title">
                Are you sure you want to{' '}
                {pendingSaveData?.chartId === '' ? 'save' : 'update'} this
                chart?
              </p>
              {pendingSaveData?.titleResolution.wasRewritten && (
                <p className="confirmation-modal__message">
                  {`A chart named "${pendingSaveData.titleResolution.requestedTitle}" already exists. ` +
                    `It will be saved as "${pendingSaveData.titleResolution.uniqueTitle}". ` +
                    'Cancel if you want to choose a different title.'}
                </p>
              )}
              {pendingSaveData?.type === 'dental' &&
                pendingSaveData.dentalSummary && (
                  <DentalChartSummaryContent
                    summary={pendingSaveData.dentalSummary}
                  />
                )}
            </div>
          }
          footer={
            <div className="confirmation-modal__footer">
              <Button
                size="md"
                className="button-secondary"
                onClick={dismissSaveConfirm}
              >
                Cancel
              </Button>
              <Button
                size="md"
                className="button-primary"
                onClick={confirmSaveChart}
              >
                Confirm {pendingSaveData?.chartId === '' ? 'Save' : 'Update'}
              </Button>
            </div>
          }
        />
        <ChartingUnsavedChangesModal
          show={showUnsavedLeaveConfirm}
          onKeepEditing={handleUnsavedLeaveCancel}
          onDiscard={handleUnsavedLeaveDiscard}
        />

        {alertMessageInfo.show && (
          <AlertMessage
            show={alertMessageInfo.show}
            onHide={() =>
              setAlertMessageInfo({ ...alertMessageInfo, show: false })
            }
            title={alertMessageInfo.title}
            message={alertMessageInfo.message}
            type={alertMessageInfo.type}
          />
        )}
      </div>
    </Container>
  )
}

export default ChartingPage
